import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../../baseQuery';
import { SortOrder } from 'types/interfaces/sort';
import {
  CollectionListFetchRequest,
  CollectionListFetchResponse,
} from '../types';
import { CollectionList } from 'types/interfaces/collectionList';
import { CardCondition } from 'types/interfaces/condition/condition';
import { tradeApi } from '../../trade/tradeApi';

export interface HaveListFetchResponse extends CollectionListFetchResponse {
  haveList: CollectionList[];
}

export interface AddCardToHaveListRequest {
  cardId: number;
  quantity?: number;
  condition?: CardCondition;
  foil?: boolean;
}

export interface UpdateHaveListCardRequest {
  haveListRecordId: number;
  newCardId?: number;
  quantity?: number;
  condition?: CardCondition;
  foil?: boolean;
}

export interface HaveListBulkImportTextRequest {
  text: string;
}

export interface HaveListBulkImportResponse {
  totalRows: number;
  successCount: number;
  errorCount: number;
  errors: Array<{
    row: number;
    cardName?: string;
    error: string;
  }>;
  importedCards: Array<{
    id: number;
    cardName: string;
    quantity: number;
  }>;
}

export const haveListApi = createApi({
  reducerPath: 'haveListApi',
  baseQuery: baseQuery,
  tagTypes: ['HaveList'],
  endpoints: (builder) => ({
    haveListFetch: builder.infiniteQuery<
      HaveListFetchResponse,
      CollectionListFetchRequest,
      number,
      string
    >({
      query: ({ pageParam, queryArg }) => {
        // Build URL parameters conditionally to avoid sending empty values
        const params = new URLSearchParams({
          page: pageParam.toString(),
          limit: '10'
        });

        // Only add sort if it has a value
        if (queryArg.sort) {
          params.append('sort', queryArg.sort);
        }

        // Only add direction if it has a value (not undefined or empty string)
        // This fixes the issue where empty string parameters were causing backend parsing errors
        if (queryArg.direction) {
          params.append('direction', queryArg.direction);
        }

        return {
          url: `/have-list/me?${params.toString()}`,
          method: 'GET',
          requiresAuth: true,
        };
      },
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
          return lastPage.hasMore ? lastPage.page + 1 : undefined;
        },
      },
      providesTags: ['HaveList'],
    }),
    haveListFetchPaginated: builder.query<
      HaveListFetchResponse,
      { page: number; sort?: string; direction?: SortOrder; search?: string }
    >({
      query: ({ page, sort, direction, search }) => {
        // Build URL parameters conditionally to avoid sending empty values
        const params = new URLSearchParams({
          page: page.toString(),
          limit: '100' // 100 cards per page
        });

        // Only add sort if it has a value
        if (sort) {
          params.append('sort', sort);
        }

        // Only add direction if it has a value
        if (direction) {
          params.append('direction', direction);
        }

        // Only add search if it has a value
        if (search) {
          params.append('search', search);
        }

        return {
          url: `/have-list/me?${params.toString()}`,
          method: 'GET',
          requiresAuth: true,
        };
      },
      providesTags: ['HaveList'],
    }),
    addCardToHaveList: builder.mutation<void, AddCardToHaveListRequest>({
      query: (body) => ({
        url: '/have-list/add-card',
        method: 'POST',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['HaveList'],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(tradeApi.util.invalidateTags(['MatchIndex']));
        } catch {}
      },
    }),
    cardToHaveListQuantityUpdate: builder.mutation<
      void,
      { haveListRecordId: number; change: number; sort?: string; direction?: SortOrder }
    >({
      query: (body) => ({
        url: '/have-list/card/quantity-update',
        method: 'PATCH',
        body,
        requiresAuth: true,
      }),
      async onQueryStarted(
        { haveListRecordId, change, sort, direction },
        { dispatch, queryFulfilled, getState }
      ) {
        // Optimistic update for infinite query
        const patchResult = dispatch(
          haveListApi.util.updateQueryData(
            'haveListFetch',
            { sort, direction },
            (draft) => {
              draft.pages.forEach((page) => {
                page.totalCards = page.totalCards + change;
                page.haveList = page.haveList.map((item) =>
                  item.id === haveListRecordId
                    ? { ...item, quantity: item.quantity + change }
                    : item
                );
              });
            }
          )
        );

        // Optimistic update for paginated query - find all cached pages
        const paginatedPatches: any[] = [];
        const state = getState() as any;
        const queries = state.haveListApi?.queries || {};

        Object.keys(queries).forEach((key) => {
          if (key.startsWith('haveListFetchPaginated')) {
            const query = queries[key];
            if (query?.data) {
              const args = query.originalArgs;
              const paginatedPatch = dispatch(
                haveListApi.util.updateQueryData(
                  'haveListFetchPaginated',
                  args,
                  (draft) => {
                    draft.haveList = draft.haveList.map((item) =>
                      item.id === haveListRecordId
                        ? { ...item, quantity: item.quantity + change }
                        : item
                    );
                    // Update total cards count
                    draft.totalCards = draft.totalCards + change;
                  }
                )
              );
              paginatedPatches.push(paginatedPatch);
            }
          }
        });

        try {
          await queryFulfilled;
          // Invalidate match index cache so Send page gets fresh auto-match data
          dispatch(tradeApi.util.invalidateTags(['MatchIndex']));
        } catch (e) {
          // Roll back all optimistic updates on failure
          patchResult.undo();
          paginatedPatches.forEach(patch => patch.undo());
        }
      },
    }),
    removeCardFromHaveList: builder.mutation<
      void,
      { haveListRecordId: number; sort?: string; direction?: SortOrder }
    >({
      query: (body) => ({
        url: '/have-list/remove-card',
        method: 'DELETE',
        body: { haveListRecordId: body.haveListRecordId },
        requiresAuth: true,
      }),
      // Invalidate tags to refresh both infinite and paginated queries
      invalidatesTags: ['HaveList'],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(tradeApi.util.invalidateTags(['MatchIndex']));
        } catch {}
      },
    }),
    updateHaveListCard: builder.mutation<void, UpdateHaveListCardRequest>({
      query: (body) => ({
        url: '/have-list/card/update',
        method: 'PATCH',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['HaveList'],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(tradeApi.util.invalidateTags(['MatchIndex']));
        } catch {}
      },
    }),
    deleteAllHaveListItems: builder.mutation<{ message: string; deletedCount: number }, void>({
      query: () => ({
        url: '/have-list/delete-all',
        method: 'DELETE',
        requiresAuth: true,
      }),
      invalidatesTags: ['HaveList'],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(tradeApi.util.invalidateTags(['MatchIndex']));
        } catch {}
      },
    }),
    bulkImportHaveList: builder.mutation<HaveListBulkImportResponse, FormData | HaveListBulkImportTextRequest>({
      query: (body) => ({
        url: '/have-list/bulk-import',
        method: 'POST',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['HaveList'],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(tradeApi.util.invalidateTags(['MatchIndex']));
        } catch {}
      },
    }),
  }),
});

export const {
  useHaveListFetchInfiniteQuery,
  useHaveListFetchPaginatedQuery,
  useAddCardToHaveListMutation,
  useCardToHaveListQuantityUpdateMutation,
  useRemoveCardFromHaveListMutation,
  useUpdateHaveListCardMutation,
  useDeleteAllHaveListItemsMutation,
  useBulkImportHaveListMutation,
} = haveListApi;
